import React, { useState } from 'react';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import { doc, collection, addDoc, updateDoc, increment, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import { useUser } from '../../contexts/UserContext';
import logger from '../../utils/logger';

const TrainingLogger = ({ onSave, onCancel }) => {
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        context: '',
        duration: 30,
        volume: '', // balls
        focusTags: [],
        rating: 3
    });

    const updateData = (key, value) => {
        setData({ ...data, [key]: value });
    };

    const toggleTag = (tag) => {
        if (data.focusTags.includes(tag)) {
            setData({ ...data, focusTags: data.focusTags.filter(t => t !== tag) });
        } else {
            if (data.focusTags.length < 3) {
                setData({ ...data, focusTags: [...data.focusTags, tag] });
            }
        }
    };

    const [error, setError] = useState(null);

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            // 1. Calculate XP based on duration/volume
            // 15 xp per 10 mins
            const xp = Math.floor((data.duration / 10) * 15);

            // 2. Create Log Entry — flattened path: Users/{userId}/TrainingSessions/{sessionId}
            // Ensure TrainingProfile/current exists first
            const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
            const profileDoc = await getDoc(trainingProfileRef);
            if (!profileDoc.exists()) {
                await setDoc(trainingProfileRef, {
                    grindScore: 0,
                    totalXp: 0,
                    role: '',
                    weeklyPlan: []
                });
            }

            // Compute summary at write time (VRCricket SessionSummary pattern)
            const sessionsRef = collection(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_SESSIONS);
            await addDoc(sessionsRef, {
                date: new Date().toISOString(),
                type: 'training',
                data: data,
                xpGained: xp,
                summary: {
                    context: data.context,
                    duration: data.duration,
                    volume: parseInt(data.volume) || 0,
                    rating: data.rating,
                    focusTags: data.focusTags
                }
            });

            // 3. Update User Stats — flattened paths
            const trainingStatsRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.STATS.BASE, FIRESTORE_PATHS.STATS.WEB_TRAINING);
            
            await updateDoc(trainingProfileRef, {
                grindScore: increment(2), // Smaller boost for training
                totalXp: increment(xp)
            });
            
            // Initialize stats if doesn't exist, then increment
            const statsDoc = await getDoc(trainingStatsRef);
            if (!statsDoc.exists()) {
                await setDoc(trainingStatsRef, { 
                    trainingSessions: 0,
                    matchesPlayed: 0,
                    totalRuns: 0,
                    totalBallsFaced: 0
                });
            }
            await updateDoc(trainingStatsRef, {
                trainingSessions: increment(1)
            });

            onSave();
        } catch (error) {
            logger.error('TrainingLogger', 'Error saving training log:', error);
            setError(`Failed to save: ${error.code || error.message}.`);
        } finally {
            setLoading(false);
        }
    };

    const focusOptions = [
        'Head Position', 'High Elbow', 'Playing Spin', 'Power Hitting',
        'Strike Rotation', 'Defense', 'Leaves', 'Footwork'
    ];

    return (
        <div className="h-full bg-gray-900 flex flex-col">
            <div className="p-4 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
                <button onClick={onCancel} className="text-gray-400"><ArrowLeft /></button>
                <h2 className="text-white font-bold">Log Training</h2>
                <div className="w-6"></div>
            </div>

            {error && (
                <div className="bg-red-500/10 border border-red-500 p-4 m-4 rounded-lg text-red-500 text-sm">
                    {error}
                </div>
            )}

            <div className="flex-1 p-6 overflow-y-auto space-y-6">

                {/* Context */}
                <div>
                    <label className="text-gray-400 text-sm block mb-2">Session Type</label>
                    <select
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                        value={data.context}
                        onChange={(e) => updateData('context', e.target.value)}
                    >
                        <option value="">Select Context...</option>
                        <option value="Open Nets">Open Nets</option>
                        <option value="Machine">Machine / Throwdowns</option>
                        <option value="Solo">Solo / Shadow Drills</option>
                        <option value="Match Sim">Match Simulation</option>
                    </select>
                </div>

                {/* Volume */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-gray-400 text-sm block mb-2">Duration (Mins)</label>
                        <input
                            type="number"
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white font-mono"
                            value={data.duration}
                            onChange={(e) => updateData('duration', parseInt(e.target.value))}
                        />
                    </div>
                    <div>
                        <label className="text-gray-400 text-sm block mb-2">Balls Faced (Est.)</label>
                        <input
                            type="number"
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white font-mono"
                            placeholder="0"
                            value={data.volume}
                            onChange={(e) => updateData('volume', e.target.value)}
                        />
                    </div>
                </div>

                {/* Focus Tags */}
                <div>
                    <label className="text-gray-400 text-sm block mb-2">What did you work on? (Max 3)</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {focusOptions.map(tag => (
                            <button
                                key={tag}
                                onClick={() => toggleTag(tag)}
                                className={`px-3 py-1 rounded-full text-xs transition-colors ${data.focusTags.includes(tag)
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-800 border border-gray-600 text-gray-400'
                                    }`}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Rating */}
                <div>
                    <label className="text-gray-400 text-sm block mb-2">Session Rating</label>
                    <div className="flex justify-between bg-gray-800 p-2 rounded-lg">
                        {[1, 2, 3, 4, 5].map(r => (
                            <button
                                key={r}
                                onClick={() => updateData('rating', r)}
                                className={`w-10 h-10 rounded-lg font-bold ${data.rating === r
                                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30'
                                    : 'text-gray-500 hover:bg-gray-700'
                                    }`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                </div>

            </div>

            <div className="p-6 bg-gray-800 border-t border-gray-700">
                <button
                    onClick={handleSave}
                    disabled={loading || !data.context}
                    className="w-full bg-green-500 text-white font-bold py-4 rounded-xl hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {loading ? 'Saving...' : <><Save size={20} /> Save Session</>}
                </button>
            </div>
        </div>
    );
};

export default TrainingLogger;
